// 12 widget skeletons — display-only stand-ins for the v6 widgets.
//
// Each renders the directive's props + a "Phase 6 wires the full
// flow" footer. The shape lets a cell template's `:::widget[X]:::`
// land WITHOUT crashing the renderer or showing the "Unknown widget"
// fallback. The skeleton's job is to:
//   1. Confirm the widget is registered + props arrived
//   2. Show the user what this widget is FOR
//   3. Hand off to Phase 6 (live wiring) without changing call sites
//
// Each skeleton accepts the registry's `WidgetProps` (string-typed
// directive attributes plus injected runtime props). Phase 6
// implementations swap in inside the same component name; the
// directive call site stays unchanged.

import type { ReactNode } from 'react';
import type { Address } from 'viem';
import type { WidgetProps } from './registry';
import { Button } from '@rmint/ui';
import {
  useUsdcBalance,
  useTokenSaleStatus,
  formatUsdc,
  onchainAddresses,
} from '../../lib/onchain';
import { env } from '@rmint/shell';

// ─── Shared chrome ───────────────────────────────────────────────

function WidgetShell({
  title,
  intent,
  children,
  cta,
  footer,
}: {
  title: string;
  intent: 'info' | 'action' | 'success' | 'warning';
  children?: ReactNode;
  cta?: ReactNode;
  footer?: string;
}) {
  const tone = {
    info: 'border-border bg-card',
    action: 'border-primary/30 bg-primary/5',
    success: 'border-success-line bg-success-bg',
    warning: 'border-warn-line bg-warn-bg',
  }[intent];
  return (
    <section className={`my-4 rounded-xl border p-5 ${tone}`}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <span className="text-[10px] uppercase tracking-wider text-foreground-mute">
          widget
        </span>
      </div>
      {children ? <div className="space-y-2 text-sm text-foreground/90">{children}</div> : null}
      {cta ? <div className="mt-4 flex items-center gap-2">{cta}</div> : null}
      <p className="mt-4 pt-3 border-t border-border/60 text-[11px] text-foreground-mute">
        {footer ?? 'Phase 6 wires the full flow.'}
      </p>
    </section>
  );
}

function PropsTable({ props }: { props: Record<string, unknown> }) {
  const entries = Object.entries(props).filter(
    ([k]) => !['widgetId', 'currentNodeId', 'walletAddress', 'onComplete'].includes(k),
  );
  if (entries.length === 0) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-foreground-mute hover:text-foreground">
        directive props ({entries.length})
      </summary>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-[11px]">
        {entries.map(([k, v]) => (
          <ItemRow key={k} k={k} v={v} />
        ))}
      </dl>
    </details>
  );
}

function ItemRow({ k, v }: { k: string; v: unknown }) {
  return (
    <>
      <dt className="text-foreground-mute">{k}</dt>
      <dd className="text-foreground break-all">
        {typeof v === 'string' ? v : JSON.stringify(v)}
      </dd>
    </>
  );
}

// ─── 1. tx-sign ─────────────────────────────────────────────────

export function TxSignSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Sign transaction"
      intent="action"
      cta={<Button variant="primary" size="sm" disabled>Review &amp; sign</Button>}
    >
      <p>
        Action <code className="text-xs">{(props.action as string) ?? '—'}</code> on
        spender <code className="text-xs">{(props.spender as string) ?? '—'}</code>.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 2. kyc-upload ──────────────────────────────────────────────

export function KycUploadSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Identity verification"
      intent="action"
      cta={<Button variant="primary" size="sm" disabled>Open Stripe Identity</Button>}
    >
      <p>
        Upload your government ID + selfie. Stripe Identity returns a verification
        result and the projector emits <code className="text-xs">kyc.submitted</code>.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 4. fund-wallet ─────────────────────────────────────────────

export function FundWalletSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Fund your wallet"
      intent="action"
      cta={
        <>
          <Button variant="primary" size="sm" disabled>Coinbase Onramp</Button>
          <Button variant="inverse" size="sm" disabled>MoonPay</Button>
        </>
      }
    >
      <p>USDC fiat on-ramp. Lands USDC in your smart wallet.</p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 5. create-subscription ─────────────────────────────────────

export function CreateSubscriptionSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Start an investment"
      intent="action"
      cta={<Button variant="primary" size="sm" disabled>Begin subscription</Button>}
    >
      <p>
        Creates a subscription entity for{' '}
        <code className="text-xs">{(props.contractAddress as string) ?? '—'}</code> and routes you
        through review → payment → sign → custody.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 6. review ──────────────────────────────────────────────────

export function ReviewSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Review subscription"
      intent="info"
      cta={<Button variant="primary" size="sm" disabled>Confirm</Button>}
    >
      <p>
        Confirm your subscription amount + restaurant. Phase 6 reads the live
        offering state to gate the confirm button on accreditation, hard cap, and
        sale window.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 7. payment ─────────────────────────────────────────────────
//
// Phase 7 §7.4: live sale status + investor's current USDC balance
// via useTokenSaleStatus + useUsdcBalance. Action buttons remain
// disabled in this skeleton; the live tx flow is the EIP-712 +
// approve/buy track scheduled for Phase 8.

export function PaymentSkeleton(props: WidgetProps) {
  const wallet = (props.walletAddress as string | undefined) ?? null;
  const saleAddress = onchainAddresses.sakuraSale;

  const sale = useTokenSaleStatus(saleAddress);
  const balance = useUsdcBalance(wallet as Address | null);

  let saleProgress: string;
  if (sale.error) saleProgress = '(sale read failed)';
  else if (sale.loading || !sale.data) saleProgress = 'Loading…';
  else saleProgress = `${formatUsdc(sale.data.totalRaised)} / ${formatUsdc(sale.data.hardCap)} raised`;

  let walletBalance: string;
  if (!wallet) walletBalance = '—';
  else if (balance.error) walletBalance = '(read failed)';
  else if (balance.loading || !balance.data) walletBalance = 'Loading…';
  else walletBalance = formatUsdc(balance.data.balance, balance.data.decimals);

  return (
    <WidgetShell
      title="Payment — USDC approve + buy"
      intent="action"
      cta={
        <>
          <Button variant="primary" size="sm" disabled>Approve USDC</Button>
          <Button variant="inverse" size="sm" disabled>Buy</Button>
        </>
      }
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mb-2">
        <dt className="text-foreground-mute">Your USDC</dt>
        <dd>{walletBalance}</dd>
        <dt className="text-foreground-mute">Sale progress</dt>
        <dd>{saleProgress}</dd>
        <dt className="text-foreground-mute">Status</dt>
        <dd>{sale.data?.finalized ? 'Finalized' : sale.data ? 'Live' : '—'}</dd>
      </dl>
      <p className="text-foreground-mute text-xs">
        Approves the sale contract for the subscription amount, then calls{' '}
        <code className="text-xs">buy(...)</code>. Phase 8 wires the live
        approve + buy transactions through the EIP-712 / Smart-Wallet flow.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 8. subscription-sign ───────────────────────────────────────

export function SubscriptionSignSkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Sign subscription agreement"
      intent="action"
      cta={<Button variant="primary" size="sm" disabled>Sign (EIP-712)</Button>}
    >
      <p>
        EIP-712 typed-data signature over the subscription terms. The signed
        envelope lands as a <code className="text-xs">subscription.signed</code> event;
        the projector projects the signed PDF artifact into the dossier.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 9. custody-safe ────────────────────────────────────────────

export function CustodySafeSkeleton(props: WidgetProps) {
  const safeAddress = (props.safeAddress as string) ?? '—';
  return (
    <WidgetShell title="Custody Safe" intent="info">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-foreground-mute">Address</dt>
        <dd className="font-mono break-all">{safeAddress}</dd>
        <dt className="text-foreground-mute">Whitelist</dt>
        <dd>—</dd>
        <dt className="text-foreground-mute">RMOT held</dt>
        <dd>—</dd>
        <dt className="text-foreground-mute">Admin lock</dt>
        <dd>—</dd>
      </dl>
      <p className="text-foreground-mute">
        Phase 6 wires viem reads against the Safe contract for live balances + tx history.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 10. awaiting-finalization ──────────────────────────────────

export function AwaitingFinalizationSkeleton(props: WidgetProps) {
  return (
    <WidgetShell title="Awaiting finalization" intent="info">
      <p>
        Sale progress: <strong>—%</strong> subscribed · expected close{' '}
        <strong>—</strong>.
      </p>
      <p className="text-foreground-mute text-xs">
        Phase 6 polls the sale contract; Phase 5 reads the latest state from the dossier.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 11. token-delivery ─────────────────────────────────────────

export function TokenDeliverySkeleton(props: WidgetProps) {
  return (
    <WidgetShell
      title="Token delivery"
      intent="success"
      cta={<Button variant="primary" size="sm" disabled>Claim RMOT</Button>}
    >
      <p>
        Sale finalized. Click claim to mint your RMOT into the custody Safe.
        Phase 6 wires the on-chain claim transaction; Phase 5 displays the receipt
        once the projector emits <code className="text-xs">distribution.received</code>.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 13. form-invest ────────────────────────────────────────────
//
// Phase 7 §7.4: the offering-page "invest now" form. Referenced by
// content/.../02-offering-overview.md as:
//   :::widget[form-invest]{variant="panel" contract="<saleAddress>"
//     min="<minUsdc>" max="<maxUsdc>" tokenPrice="<priceUsdc>"}:::
//
// Live data:
//   - useTokenSaleStatus(props.contract) — totalRaised / hardCap /
//     finalized status from the sale contract
//   - useUsdcBalance(walletAddress) — investor's available USDC
//
// The "Approve USDC + Buy" buttons remain disabled here; the live
// transaction flow lands in Phase 8 with the EIP-712 + Smart Wallet
// integration. This skeleton's job is to surface enough state for
// the investor to evaluate the opportunity AND to confirm Slice E's
// on-chain reads succeed against the real deployed sale.

export function FormInvestSkeleton(props: WidgetProps) {
  const wallet = (props.walletAddress as string | undefined) ?? null;
  const contract = (props.contract as string | undefined) ?? null;
  const min = (props.min as string | undefined) ?? '—';
  const max = (props.max as string | undefined) ?? '—';
  const tokenPrice = (props.tokenPrice as string | undefined) ?? '—';

  const sale = useTokenSaleStatus(
    contract && /^0x[a-fA-F0-9]{40}$/.test(contract) ? (contract as Address) : null,
  );
  const balance = useUsdcBalance(wallet as Address | null);

  let saleProgress: string;
  if (!contract) saleProgress = 'No sale contract address';
  else if (sale.error) saleProgress = '(sale read failed)';
  else if (sale.loading || !sale.data) saleProgress = 'Loading…';
  else
    saleProgress = `${formatUsdc(sale.data.totalRaised)} / ${formatUsdc(sale.data.hardCap)} raised`;

  let walletBalance: string;
  if (!wallet) walletBalance = 'Connect wallet to see balance';
  else if (balance.error) walletBalance = '(read failed)';
  else if (balance.loading || !balance.data) walletBalance = 'Loading…';
  else walletBalance = formatUsdc(balance.data.balance, balance.data.decimals);

  let saleStatusLabel = '—';
  if (sale.data) saleStatusLabel = sale.data.finalized ? 'Finalized' : 'Live';

  return (
    <WidgetShell
      title="Invest in this offering"
      intent="action"
      cta={
        <>
          <Button variant="primary" size="sm" disabled>
            Approve USDC
          </Button>
          <Button variant="inverse" size="sm" disabled>
            Buy
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mb-2">
        <dt className="text-foreground-mute">Status</dt>
        <dd>{saleStatusLabel}</dd>
        <dt className="text-foreground-mute">Sale progress</dt>
        <dd>{saleProgress}</dd>
        <dt className="text-foreground-mute">Token price</dt>
        <dd>{tokenPrice} USDC</dd>
        <dt className="text-foreground-mute">Min / Max</dt>
        <dd>
          {min} / {max} USDC
        </dd>
        <dt className="text-foreground-mute">Your USDC</dt>
        <dd>{walletBalance}</dd>
      </dl>
      <p className="text-foreground-mute text-xs">
        Approve USDC for the sale contract, then buy. Phase 8 wires the
        EIP-712 sign + approve/buy transaction flow against the user's
        Smart Wallet.
      </p>
      <PropsTable props={props} />
    </WidgetShell>
  );
}

// ─── 12. wallet-overview ────────────────────────────────────────
//
// Phase 7 §7.4: live USDC balance via useUsdcBalance hook. The hook
// returns null while loading and { balance, decimals } once resolved.
// Module-level cache (15s TTL) deduplicates concurrent reads when
// multiple widgets render the same wallet.

function networkLabel(): string {
  switch (env.chainEnv) {
    case 'local':
      return 'Local (Anvil)';
    case 'base':
      return 'Base';
    case 'base-sepolia':
    default:
      return 'Base Sepolia';
  }
}

export function WalletOverviewSkeleton(props: WidgetProps) {
  const wallet = (props.walletAddress as string | undefined) ?? null;
  const { data, loading, error } = useUsdcBalance(wallet as Address | null);

  let usdcDisplay: string;
  if (!wallet) {
    usdcDisplay = '—';
  } else if (error) {
    usdcDisplay = '(read failed)';
  } else if (loading || !data) {
    usdcDisplay = 'Loading…';
  } else {
    usdcDisplay = formatUsdc(data.balance, data.decimals);
  }

  return (
    <WidgetShell title="Smart wallet" intent="info">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-foreground-mute">Address</dt>
        <dd className="font-mono break-all">{wallet ?? '—'}</dd>
        <dt className="text-foreground-mute">USDC balance</dt>
        <dd>{usdcDisplay}</dd>
        <dt className="text-foreground-mute">Network</dt>
        <dd>{networkLabel()}</dd>
      </dl>
      <PropsTable props={props} />
    </WidgetShell>
  );
}
